/**
 * Pendências de conciliação — e as duas saídas para cada uma.
 *
 * **Reprocessar** vale quando a causa foi transitória: o Bioslab fora do ar, a
 * rede bloqueada. Insistir resolve, e a consulta vai só às guias marcadas —
 * reconciliar o arquivo inteiro significaria consultar a produção de terceiro
 * por centenas de guias já resolvidas.
 *
 * **Resolver à mão** vale quando a causa é definitiva: a guia não existe no
 * Bioslab com aquele número, ou resolveu para mais de um protocolo. Aí
 * reprocessar mil vezes dá no mesmo, e alguém precisa consultar o Bioslab pela
 * tela e informar o vínculo — que fica marcado como manual para sempre.
 *
 * A tela separa os dois casos de propósito. Oferecer "reprocessar" para uma
 * guia que não existe é oferecer trabalho inútil com aparência de progresso.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { mensagemErro } from '../api/axios';
import {
  reprocessarPendencias,
  consultarGuiaPrestador,
  resolverPendenciaManual,
  visaoPendencias,
} from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Busca,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { usePaginacao } from '../hooks/usePaginacao';
import { brl, formatDate, formatDateTime, numero } from '../utils/formato';
import { MOTIVO_PENDENCIA } from '../utils/opcoes';

/* ── Resolução manual ───────────────────────────────────────────────────── */
/**
 * Resolver à mão — em branco, ou já preenchido pela 2ª via de guia.
 *
 * `achado` chega quando o usuário clicou em "Tentar por guia alternativa" e o
 * Bioslab encontrou algo. O formulário é o mesmo de propósito: o que muda é
 * a origem de onde vieram os valores, e **quem confirma continua sendo a
 * pessoa**. Um número de guia digitado errado pode, por coincidência, ser o
 * número válido de outra guia real — aceitar sozinho vincularia dinheiro ao
 * protocolo errado, sem que nenhuma conferência percebesse.
 */
function ModalResolverManual({ pendencia, achado, aoFechar, aoResolver }) {
  const [protocolo, setProtocolo] = useState(achado?.protocolo || '');
  const [valorCobrado, setValorCobrado] = useState(
    achado?.valor_cobrado != null ? String(achado.valor_cobrado) : ''
  );
  const [nrFatura, setNrFatura] = useState(achado?.nr_fatura || '');
  const [justificativa, setJustificativa] = useState(
    achado?.justificativa_sugerida || ''
  );
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const extras = Object.entries(pendencia.dados_extracao || {});

  async function salvar(e) {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await resolverPendenciaManual(pendencia.id_pendencia, {
        protocolo: protocolo.trim(),
        justificativa: justificativa.trim(),
        valor_cobrado: valorCobrado ? Number(valorCobrado) : null,
        nr_fatura: nrFatura.trim() || null,
        // Muda a origem gravada: digitado do zero e confirmado a partir da
        // 2a via de guia sao coisas diferentes para quem auditar depois.
        via_guia_prestador: !!achado,
      });
      aoResolver();
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível resolver a pendência.'));
      setSalvando(false);
    }
  }

  return (
    <>
      <div
        className="modal-backdrop fade show"
        onClick={aoFechar}
        style={{ background: 'rgba(16,24,40,.5)' }}
      />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
          {/* O aviso fica DENTRO do modal, colado nos campos que ele explica —
              não num toast que some antes de a pessoa ler. */}
          <div
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Resolver manualmente</h2>
                <p className="ff-painel__sub">
                  Guia <strong className="ff-mono">{pendencia.guia}</strong> ·{' '}
                  {brl(pendencia.valor_pago)}
                </p>
              </div>
              <button className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <form onSubmit={salvar} className="modal-body d-flex flex-column gap-3">
          <Alerta tipo="erro" onFechar={() => setErro('')}>
            {erro}
          </Alerta>

          {achado ? (
            <Alerta tipo="atencao">
              Preenchido pela <strong>guia alternativa</strong> ({achado.guia_prestador}),
              que o Bioslab reconheceu — a conciliação já havia tentado sem sucesso pelo
              número {achado.guia_original}. <strong>Confira antes de confirmar:</strong>{' '}
              um número de guia digitado errado pode, por coincidência, ser o número
              válido de outra guia real — e aí o dinheiro iria para o protocolo errado
              sem que nenhuma conferência percebesse. É por isso que a conciliação não
              tenta este número sozinha.
              {achado.qtd_itens ? ` O Bioslab devolveu ${achado.qtd_itens} item(ns).` : ''}
            </Alerta>
          ) : (
            <Alerta tipo="atencao">
              O vínculo informado aqui fica <strong>marcado como manual</strong> e a marca
              acompanha o dado, inclusive na baixa por protocolo. É o que permite
              distinguir, meses depois, o que veio do Bioslab do que foi digitado.
            </Alerta>
          )}

          {extras.length > 0 && (
            <div className="ff-campo">
              <label>O que a extração leu para esta guia</label>
              <div
                style={{
                  fontSize: 13,
                  background: 'var(--ff-surface-2, #f6f8fa)',
                  borderRadius: 8,
                  padding: '10px 12px',
                }}
              >
                {extras.map(([chave, valor]) => (
                  <div key={chave} className="d-flex gap-2">
                    <span style={{ color: 'var(--ff-muted)' }}>{chave}:</span>
                    <span className="ff-mono">{String(valor)}</span>
                  </div>
                ))}
              </div>
              <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                Se houver um segundo número de guia, ele costuma ser o motivo de o Bioslab
                não encontrar a primeira.
              </span>
            </div>
          )}

          <div className="ff-campo">
            <label htmlFor="protocolo">Protocolo *</label>
            <input
              id="protocolo"
              className="ff-input ff-input--valor"
              value={protocolo}
              onChange={(e) => setProtocolo(e.target.value)}
              placeholder="476.03.2026"
              required
              disabled={salvando}
            />
            <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
              Consulte no Bioslab e informe o protocolo ao qual esta guia pertence.
            </span>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <div className="ff-campo">
                <label htmlFor="valor-cobrado">Valor cobrado (opcional)</label>
                <input
                  id="valor-cobrado"
                  className="ff-input ff-input--valor"
                  type="number"
                  step="0.01"
                  value={valorCobrado}
                  onChange={(e) => setValorCobrado(e.target.value)}
                  disabled={salvando}
                />
                <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
                  O <code>vlr_contratante</code> do Bioslab. Informando, a divergência
                  cobrado × pago passa a aparecer também para esta guia.
                </span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="ff-campo">
                <label htmlFor="nr-fatura">Fatura (opcional)</label>
                <input
                  id="nr-fatura"
                  className="ff-input"
                  value={nrFatura}
                  onChange={(e) => setNrFatura(e.target.value)}
                  disabled={salvando}
                />
              </div>
            </div>
          </div>

          <div className="ff-campo">
            <label htmlFor="justificativa">Por que está sendo resolvida à mão? *</label>
            <textarea
              id="justificativa"
              className="ff-input"
              rows={3}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder="Ex.: guia com numeração divergente no arquivo; protocolo conferido na tela do Bioslab."
              required
              minLength={10}
              disabled={salvando}
            />
            <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
              Obrigatório. Sem o motivo registrado, daqui a alguns meses existe um vínculo
              digitado à mão e ninguém sabe com base em quê — e esse vínculo vira baixa.
            </span>
          </div>

          <div className="d-flex gap-2">
            <button
              type="submit"
              className="ff-btn ff-btn--primario"
              disabled={salvando || !protocolo.trim() || justificativa.trim().length < 10}
            >
              {salvando ? 'Salvando…' : 'Vincular ao protocolo'}
            </button>
              <Botao variante="secundario" onClick={aoFechar} disabled={salvando}>
                Cancelar
              </Botao>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
}

export default function Pendencias() {
  const [itens, setItens] = useState([]);
  const [resumo, setResumo] = useState({});
  const [motivo, setMotivo] = useState('');
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');

  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [reprocessando, setReprocessando] = useState(false);
  const [emResolucao, setEmResolucao] = useState(null);
  // O que o Bioslab devolveu pela 2a via, para pre-preencher o modal.
  const [achado, setAchado] = useState(null);
  const [consultando, setConsultando] = useState('');

  const carregar = useCallback(() => {
    setCarregando(true);
    visaoPendencias(motivo ? { motivo } : undefined)
      .then((r) => {
        setItens(r.data.itens);
        setResumo(r.data.resumo);
        setSelecionadas(new Set());
      })
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar as pendências.')))
      .finally(() => setCarregando(false));
  }, [motivo]);

  useEffect(carregar, [carregar]);

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return itens;
    return itens.filter(
      (p) => p.guia?.toLowerCase().includes(t) || p.arquivo?.toLowerCase().includes(t)
    );
  }, [itens, busca]);

  const ord = useOrdenacao(filtrados, 'ultima_tentativa_em', false);
  const pag = usePaginacao(ord.ordenados, 15);

  const cartoes = Object.entries(resumo);
  const totalValor = filtrados.reduce((s, p) => s + Number(p.valor_pago || 0), 0);

  function alternar(id) {
    setSelecionadas((atual) => {
      const nova = new Set(atual);
      if (nova.has(id)) nova.delete(id);
      else nova.add(id);
      return nova;
    });
  }

  const idsVisiveis = pag.paginados.map((p) => p.id_pendencia);
  const todasVisiveisMarcadas =
    idsVisiveis.length > 0 && idsVisiveis.every((id) => selecionadas.has(id));

  function alternarTodas() {
    setSelecionadas((atual) => {
      const nova = new Set(atual);
      if (todasVisiveisMarcadas) idsVisiveis.forEach((id) => nova.delete(id));
      else idsVisiveis.forEach((id) => nova.add(id));
      return nova;
    });
  }

  async function reprocessar() {
    setErro('');
    setAviso('');
    setReprocessando(true);
    try {
      const r = await reprocessarPendencias([...selecionadas]);
      const partes = [`${r.resolvidas} resolvida(s)`];
      if (r.pendentes) partes.push(`${r.pendentes} seguem pendentes`);
      if (r.erros?.length) partes.push(`erro: ${r.erros[0]}`);
      setAviso(`${r.selecionadas} pendência(s) reprocessada(s) — ${partes.join(', ')}.`);
      carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível reprocessar.'));
    } finally {
      setReprocessando(false);
    }
  }

  /**
   * Consulta o Bioslab pelo segundo número da guia.
   *
   * O resultado **não é gravado**: ele abre o mesmo formulário de resolução
   * manual, já preenchido, e a pessoa confirma. O motivo de não ser
   * automático está no alerta dentro do modal — um número digitado errado
   * pode ser o número válido de outra guia real.
   */
  async function tentarGuiaPrestador(p) {
    setErro('');
    setAviso('');
    setConsultando(p.id_pendencia);
    try {
      const { data } = await consultarGuiaPrestador(p.id_pendencia);
      if (!data.encontrou || !data.protocolo) {
        setErro(data.mensagem || 'O Bioslab não reconheceu o número do prestador.');
        return;
      }
      setAchado(data);
      setEmResolucao(p);
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível consultar o Bioslab.'));
    } finally {
      setConsultando('');
    }
  }

  // Reprocessar só ajuda quando a causa foi transitória. Saber quantas das
  // selecionadas são definitivas evita prometer progresso que não vem.
  const selecionadasDefinitivas = filtrados.filter(
    (p) => selecionadas.has(p.id_pendencia) && !p.transitorio
  ).length;

  return (
    <>
      <PageHeader
        titulo="Pendências de conciliação"
        subtitulo="Guias extraídas de um arquivo que ainda não chegaram a um protocolo."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      {cartoes.length > 0 && (
        <div className="ff-grid-tiles mb-4">
          {cartoes.map(([chave, dados]) => {
            const cfg = MOTIVO_PENDENCIA[chave] || {
              label: chave,
              natureza: 'neutro',
              ajuda: '',
            };
            const ativo = motivo === chave;
            return (
              <button
                key={chave}
                type="button"
                className={`ff-tile ff-tile--${cfg.natureza}`}
                onClick={() => setMotivo(ativo ? '' : chave)}
                aria-pressed={ativo}
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  boxShadow: ativo ? '0 0 0 2px var(--ff-blue)' : undefined,
                }}
              >
                <span className="rotulo">{cfg.label}</span>
                <span className="valor">{numero(dados.qtd)}</span>
                <span className="nota">{brl(dados.valor)}</span>
                <span className="nota" style={{ fontSize: 12, lineHeight: 1.4 }}>
                  {cfg.ajuda}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {selecionadas.size > 0 && (
        <div
          className="mb-3 d-flex flex-wrap gap-3 align-items-center justify-content-between"
          style={{
            background: 'var(--ff-surface)',
            border: '1px solid var(--ff-border)',
            borderRadius: 12,
            padding: '12px 16px',
          }}
        >
          <span>
            <strong>{selecionadas.size}</strong> pendência
            {selecionadas.size === 1 ? '' : 's'} selecionada
            {selecionadas.size === 1 ? '' : 's'}
          </span>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {selecionadasDefinitivas > 0 && (
              <span style={{ fontSize: 13, color: '#9a5b06' }}>
                <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
                {selecionadasDefinitivas} não se resolve
                {selecionadasDefinitivas === 1 ? '' : 'm'} por nova tentativa
              </span>
            )}
            <button
              type="button"
              className="ff-btn ff-btn--primario"
              onClick={reprocessar}
              disabled={reprocessando}
            >
              {reprocessando ? 'Consultando o Bioslab…' : 'Reprocessar selecionadas'}
            </button>
            <Botao variante="secundario" onClick={() => setSelecionadas(new Set())}>
              Limpar seleção
            </Botao>
          </div>
        </div>
      )}

      <Painel
        titulo={motivo ? MOTIVO_PENDENCIA[motivo]?.label || 'Pendências' : 'Todas as pendências'}
        subtitulo={
          filtrados.length > 0
            ? `${numero(filtrados.length)} guia${filtrados.length === 1 ? '' : 's'} · ${brl(totalValor)}`
            : undefined
        }
        busca={
          itens.length > 0 && (
            <>
              <Busca
                valor={busca}
                onChange={setBusca}
                placeholder="Buscar guia ou arquivo"
                id="busca-pendencias"
              />
              {motivo && (
                <button
                  type="button"
                  className="ff-btn ff-btn--fantasma"
                  onClick={() => setMotivo('')}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                  Limpar filtro
                </button>
              )}
            </>
          )
        }
        rodape={filtrados.length > 0 && <Paginacao {...pag} rotulo="pendências" />}
      >
        {carregando ? (
          <LoadingSpinner />
        ) : itens.length === 0 ? (
          <Vazio
            icone="bi-check2-circle"
            titulo={motivo ? 'Nenhuma pendência com esse motivo' : 'Nenhuma pendência'}
            descricao="Guias aparecem aqui quando não são encontradas no Bioslab, não resolvem para um protocolo, ou quando a consulta falha."
          />
        ) : filtrados.length === 0 ? (
          <Vazio
            icone="bi-search"
            titulo="Nenhuma pendência corresponde à busca"
            descricao={`Nada encontrado para "${busca}".`}
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={todasVisiveisMarcadas}
                    onChange={alternarTodas}
                    aria-label="Selecionar todas desta página"
                  />
                </Th>
                <Th campo="guia" estado={ord}>
                  Guia
                </Th>
                <Th campo="valor_pago" estado={ord} alinhar="right">
                  Valor pago
                  <Ajuda titulo="Valor pago" origem="Arquivo da operadora · Valor (R$)">
                    Valor que a operadora pagou nesta guia. Como ela não resolveu para um
                    protocolo, este valor NÃO está somado em nenhuma baixa.
                  </Ajuda>
                </Th>
                <Th campo="motivo" estado={ord}>
                  Motivo
                  <Ajuda titulo="Por que ficou pendente">
                    Cada motivo pede uma ação diferente: indisponibilidade só precisa de
                    nova tentativa; guia não encontrada e múltiplos protocolos exigem
                    decisão humana. Veja a Documentação, seção 9.
                  </Ajuda>
                </Th>
                <Th>Detalhe da última tentativa</Th>
                <Th campo="tentativas" estado={ord} alinhar="right">
                  Tentativas
                  <Ajuda titulo="Tentativas">
                    Quantas vezes esta guia já foi consultada. O motivo e a mensagem ao
                    lado são sempre os da <strong>última</strong> tentativa — se o número
                    sobe e a mensagem não muda, insistir não vai resolver.
                  </Ajuda>
                </Th>
                <Th campo="arquivo" estado={ord}>
                  Arquivo
                </Th>
                <Th campo="ultima_tentativa_em" estado={ord} alinhar="right">
                  Última tentativa
                </Th>
                <Th alinhar="right">Ação</Th>
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((p) => {
                const cfg = MOTIVO_PENDENCIA[p.motivo] || {
                  label: p.motivo,
                  tom: 'arquivado',
                };
                return (
                  <tr key={p.id_pendencia}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selecionadas.has(p.id_pendencia)}
                        onChange={() => alternar(p.id_pendencia)}
                        aria-label={`Selecionar guia ${p.guia}`}
                      />
                    </td>
                    <td className="col-codigo">{p.guia}</td>
                    <td className="col-valor">{brl(p.valor_pago)}</td>
                    <td>
                      <Badge tom={cfg.tom}>{cfg.label}</Badge>
                      {!p.transitorio && (
                        <div style={{ fontSize: 12, color: 'var(--ff-muted)', marginTop: 2 }}>
                          nova tentativa não resolve
                        </div>
                      )}
                    </td>
                    <td
                      className="col-texto"
                      style={{ fontSize: 13, color: 'var(--ff-muted)' }}
                    >
                      {p.detalhe || '—'}
                    </td>
                    <td className="col-qtd">{p.tentativas}</td>
                    <td className="col-meta">{p.arquivo}</td>
                    <td className="col-data" title={formatDateTime(p.ultima_tentativa_em)}>
                      {formatDate(p.ultima_tentativa_em)}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div className="d-flex gap-2 justify-content-end align-items-center">
                        {/* Só aparece quando há um SEGUNDO número a tentar.
                            A conciliação consulta o Bioslab pela guia do
                            prestador (`Guia Prest.`/`Guia Contratado`), que é
                            a guia do laboratório; o que sobra para este botão
                            é a guia principal do arquivo — a que o sistema
                            deliberadamente não perguntou, para não casar com
                            outra guia real por coincidência. */}
                        {p.dados_extracao?.guia_prestador &&
                          p.dados_extracao.guia_prestador !== p.guia && (
                            <Botao
                              variante="secundario"
                              pequeno
                              onClick={() => tentarGuiaPrestador(p)}
                              disabled={consultando === p.id_pendencia}
                              title={
                                `Consultar o Bioslab pelo número ${p.guia} — a guia ` +
                                'do arquivo, que a conciliação NÃO tentou. Ela ' +
                                `consultou pela guia do prestador ` +
                                `(${p.dados_extracao.guia_prestador}), que é a guia do ` +
                                'laboratório. O resultado vira proposta na tela, nunca ' +
                                'vínculo automático.'
                              }
                            >
                              {consultando === p.id_pendencia
                                ? 'Consultando…'
                                : 'Tentar por guia alternativa'}
                            </Botao>
                          )}

                        {/* A ausência do botão precisa ser explicada, não só
                            observada: "o modelo não mapeia o campo" e "esta
                            guia não tem segundo número" são situações
                            diferentes, e pedem ações opostas — corrigir o
                            mapeamento da operadora, ou resolver à mão. */}
                        {p.motivo === 'guia_nao_encontrada' &&
                          !p.dados_extracao?.guia_prestador &&
                          p.modelo_mapeia_guia_prestador === false && (
                            <span
                              style={{ fontSize: 12, color: 'var(--ff-muted)' }}
                              title={
                                'O modelo desta operadora nao mapeia a guia ' +
                                'alternativa — o segundo numero que costuma resolver ' +
                                'guia digitada errada ("Guia Prest." na Unimed, ' +
                                '"Guia Contratado" na GEAP). Sem ele, so resta ' +
                                'resolver a mao. Para habilitar, recadastre o modelo ' +
                                'em Modelos de arquivo.'
                              }
                            >
                              <i className="bi bi-info-circle me-1" aria-hidden="true" />
                              sem guia alternativa
                            </span>
                          )}

                        <Botao
                          variante="secundario"
                          pequeno
                          onClick={() => setEmResolucao(p)}
                          title="Informar o protocolo consultando o Bioslab pela tela"
                        >
                          Resolver à mão
                        </Botao>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2}>Total{motivo || busca ? ' (filtrado)' : ''}</th>
                <th className="col-valor">{brl(totalValor)}</th>
                <th colSpan={6} />
              </tr>
            </tfoot>
          </Tabela>
        )}
      </Painel>

      {emResolucao && (
        <ModalResolverManual
          pendencia={emResolucao}
          achado={achado}
          aoFechar={() => {
            setEmResolucao(null);
            setAchado(null);
          }}
          aoResolver={() => {
            setEmResolucao(null);
            setAchado(null);
            setAviso(`Guia ${emResolucao.guia} vinculada manualmente ao protocolo.`);
            carregar();
          }}
        />
      )}
    </>
  );
}
